import React from 'react';
import PropTypes from 'prop-types';

const StepNameAndVariables = ({ 
  comparisonName, 
  setComparisonName,
  requiredVariables = [],
  optionalVariables = [],
  inputValues,
  setInputValues
}) => {
  const handleInputChange = (key, value) => {
    setInputValues(prev => ({ ...prev, [key]: value }));
  };

  const handleArrayChange = (key, index, value) => {
    setInputValues(prev => {
      const arr = prev[key] || [];
      const newArr = [...arr];
      newArr[index] = value;
      return { ...prev, [key]: newArr };
    });
  };

  const handleArrayAdd = (key) => {
    setInputValues(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: [...arr, ''] };
    });
  };

  const handleArrayRemove = (key, index) => {
    setInputValues(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.filter((_, i) => i !== index) };
    });
  };

  // Use the separate arrays directly instead of filtering by optional flag
  const requiredVars = requiredVariables;
  const optionalVars = optionalVariables;
  const projectVariables = [...requiredVariables, ...optionalVariables];

  const renderVariableInput = (variable) => {
    if (variable.type === 'Array List') {
      return (
        <div className="space-y-2">
          {(inputValues[variable.name] || ['']).map((item, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="text"
                value={item}
                onChange={(e) => handleArrayChange(variable.name, index, e.target.value)}
                placeholder={`Enter ${variable.name} item ${index + 1}`}
                className="w-full px-4 py-2.5 text-sm text-text-primary bg-bg-elevated border border-primary-700/50 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all duration-fast placeholder:text-text-tertiary shadow-inner"
              />
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => handleArrayRemove(variable.name, index)}
                  className="px-3 py-2 text-sm text-red-600 hover:text-red-700 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => handleArrayAdd(variable.name)}
            className="px-3 py-2 text-sm text-primary-600 hover:text-primary-700 border border-primary-300 rounded-md hover:bg-primary-50 transition-colors"
          >
            + Add {variable.name}
          </button>
        </div>
      );
    }
    
    return (
      <input
        type="text"
        value={inputValues[variable.name] || ''}
        onChange={(e) => handleInputChange(variable.name, e.target.value)}
        placeholder={`Enter value for ${variable.name}`}
        className="w-full px-4 py-2.5 text-sm text-text-primary bg-bg-elevated border border-primary-700/50 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all duration-fast placeholder:text-text-tertiary shadow-inner"
      />
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-text-primary">Comparison Details</h3>
        <p className="text-sm text-text-secondary">Name your comparison and provide input values</p>
      </div>

      <div className="space-y-2.5">
        <label className="block text-sm font-medium text-text-primary">
          Comparison Name <span className="text-error-500">*</span>
        </label>
        <input
          type="text"
          value={comparisonName}
          onChange={(e) => setComparisonName(e.target.value)}
          placeholder="e.g., GPT-4o vs Claude Haiku"
          className="w-full px-4 py-2.5 text-sm text-text-primary bg-bg-elevated border border-primary-700/50 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all duration-fast placeholder:text-text-tertiary shadow-inner"
          autoFocus
        />
      </div>

      {requiredVars.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-text-primary border-b border-border-primary pb-2">Required Variables</h4>
          {requiredVars.map((variable) => (
            <div key={variable.name} className="space-y-2">
              <label className="block text-sm font-medium text-text-primary">
                {variable.name} <span className="text-error-500">*</span>
              </label>
              {renderVariableInput(variable)}
            </div>
          ))}
        </div>
      )}

      {optionalVars.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-text-primary border-b border-border-primary pb-2">Optional Variables</h4>
          {optionalVars.map((variable) => (
            <div key={variable.name} className="space-y-2">
              <label className="block text-sm font-medium text-text-primary">{variable.name}</label>
              {renderVariableInput(variable)}
            </div>
          ))}
        </div>
      )}

      {projectVariables.length === 0 && (
        <div className="text-center py-6 text-text-tertiary text-sm">
          No input variables required for this project
        </div>
      )}
    </div>
  );
};

StepNameAndVariables.propTypes = {
  comparisonName: PropTypes.string.isRequired,
  setComparisonName: PropTypes.func.isRequired,
  requiredVariables: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string
  })),
  optionalVariables: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string
  })),
  inputValues: PropTypes.object.isRequired,
  setInputValues: PropTypes.func.isRequired
};

export default StepNameAndVariables;

